/* eslint-disable @typescript-eslint/no-unsafe-function-type */
import { classNames } from '@allurion/ui';
import { enhanceError } from '@allurion/utils';
import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useTrackEvent } from 'src/analytics/analytics';
import { FilePreview } from 'src/components/FilePreview';
import { toastError } from 'src/components/ui/toasts';
import { encodeFile } from 'src/helpers/file';
import { Logger } from 'src/services/Logger';
import { sendNotification } from 'src/twilio/TwilioApi';
import { MAX_MSG_LEN } from 'src/utils/constants';

import messages from './chat.messages';
import { ChatAttachButton } from './ChatAttachButton';
import { ChatSendButton } from './ChatSendButton';

import styles from './ChatInput.module.scss';

const minHeightInput = 45;
const maxHeightInput = 95;

const selector = {
  container: 'chat-input-container',
  input: 'chat-input-inner',
};

type Props = {
  uniqueName: string;
  onMessageSent: Function;
  providerName: string;
  providerId: string;
  sendMessage: Function;
};

export function ChatInput({
  uniqueName = '',
  onMessageSent = (x: boolean) => x,
  providerName,
  providerId,
  sendMessage,
}: Props) {
  const [inputValue, setInputValue] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [inputHeight, setInputHeight] = useState<number>(minHeightInput);
  const intl = useIntl();
  const { patientId } = useParams<{ patientId: string }>();
  const { trackChatMessageSent } = useTrackEvent();

  const onInputChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const {
      currentTarget: { value, scrollHeight, clientHeight },
    } = event;
    const offset = 5;
    const height = scrollHeight + offset >= clientHeight ? scrollHeight : minHeightInput;

    setInputHeight(height > maxHeightInput ? maxHeightInput : height);
    setInputValue(value);
  };

  const onSend = async () => {
    if (!(inputValue || attachmentFile) || isSending) {
      return;
    }

    setIsSending(true);

    // The outer `finally` is what puts the box back. `isSending` blanks the
    // textarea and gates this handler, so any path that leaves it set disables
    // the provider's only way of replying for the rest of the session -- and
    // `onSend` is fired from an event handler, which has nowhere to report a
    // rejection.
    try {
      let idx;

      // The send is its own step. Everything below it -- notifying the patient,
      // clearing the draft, counting the message -- is only correct once the
      // message actually exists, and a failure here used to fall through to all
      // three, leaving the provider certain they had replied.
      try {
        const name = providerName;

        if (attachmentFile) {
          const file = encodeFile(attachmentFile);

          idx = await sendMessage(file, { name, isProvider: true, msgWithText: inputValue });
          // Cleared only once the send has resolved, so a failure leaves the
          // attachment attached and the whole message retryable as it stands.
          setAttachmentFile(null);
        } else {
          idx = await sendMessage(inputValue, { name, isProvider: true });
        }
      } catch (error) {
        Logger.captureException(enhanceError(error, { prefix: 'ChatInput.sendMessage' }));
        onMessageSent(false);
        toastError(intl.formatMessage(messages.errorMessageNotSent));

        // The draft stays in the box: it is the only copy, and the provider has
        // just been told it did not send.
        return;
      }

      onMessageSent(true);
      setInputValue('');

      // Best effort, and deliberately not awaited. The message is delivered and
      // visible in the conversation whether or not the push goes out, so
      // nothing after this point may gate the send box: `sendNotification` is a
      // bare `fetch`, which has no timeout, and a request that hangs rather
      // than rejects would never reach the `finally` below -- leaving
      // `isSending` set, the textarea blanked and the handler closed for the
      // rest of the session, over a message that was already delivered.
      //
      // The rejection is still handled, so a failed push is reported and not
      // shown to the provider as a failed send, which would invite a duplicate.
      void sendNotification({
        identity: uniqueName,
        roomId: uniqueName,
        body: inputValue || ' ',
        filename: attachmentFile?.name,
      }).catch((error) => {
        Logger.captureException(enhanceError(error, { prefix: 'ChatInput.sendNotification' }));
      });

      // Last, because it is the only step whose failure costs the provider
      // nothing. It calls a third-party analytics SDK directly, and ahead of the
      // two steps above an SDK outage would have cost a delivered message its
      // push and left its text sitting in the box as if it had never sent.
      trackChatMessageSent({
        patientId: patientId || '',
        providerId,
        messageIndex: idx,
        conversationId: uniqueName,
      });
    } catch (error) {
      // Not expected: the send and the notification each have their own catch.
      // This is here so that whatever does get through -- analytics, a listener
      // passed as `onMessageSent` -- is reported rather than lost, and reaches
      // the `finally`.
      Logger.captureException(enhanceError(error, { prefix: 'ChatInput.onSend' }));
    } finally {
      setIsSending(false);
    }
  };

  const onAttachment = (file: File | null) => {
    setAttachmentFile(file);
  };

  const deleteAttachment = () => {
    setAttachmentFile(null);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const { key, shiftKey } = event;

    if (key === 'Enter' && !shiftKey) {
      onSend();
    }
  };

  const placeholder = intl.formatMessage(messages.placeholder);
  const sendPlaceholder = '...⏳';
  const value = !isSending ? inputValue : sendPlaceholder;

  const isInputFilled = value && value !== sendPlaceholder;

  return (
    <>
      <FilePreview file={attachmentFile} onDelete={deleteAttachment} />
      <div className={styles.container} data-cy={selector.container}>
        {!attachmentFile && <ChatAttachButton onClick={onAttachment} />}
        <textarea
          className={classNames(
            styles.styledInput,
            isInputFilled && styles.filled,
            (isInputFilled ? inputHeight : minHeightInput) === maxHeightInput &&
              styles.shouldOverflow
          )}
          value={value}
          onChange={onInputChange}
          placeholder={placeholder}
          maxLength={MAX_MSG_LEN}
          onKeyDown={onKeyDown}
          data-cy={selector.input}
        />
        <ChatSendButton inputValue={inputValue} onClick={onSend} file={attachmentFile} />
      </div>
    </>
  );
}
